import React from 'react';
import { toast } from 'react-toastify';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Stack, Typography } from '@mui/material';
import { AccessScope, StoreAccessSettings, storeAccessService } from '../../../../services/storeAccessService';
import { adminColors, adminRadii, adminTypography } from '../../../../styles/adminDesignTokens';
import { adminButtonSx, adminButtonToneSx, adminCheckboxSx, adminCombineSx, adminDialogActionsSx, adminDialogContentSx, adminDialogSx, adminDialogTitleSx } from '../../../../styles/adminComponentStyles';
import type { StoreUser } from '../../../../services/authService';

export default function StoreUserAccessDialog({ storeId, user, onClose }: {
    storeId: string; user: StoreUser; onClose: () => void;
}) {
    const [catalog, setCatalog] = React.useState<AccessScope[]>([]);
    const [settings, setSettings] = React.useState<StoreAccessSettings | null>(null);
    const [loadStatus, setLoadStatus] = React.useState<'loading' | 'ready' | 'error'>('loading');
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const role = settings?.role ?? user.role;
    React.useEffect(() => {
        let cancelled = false;
        Promise.all([storeAccessService.catalog(storeId), storeAccessService.get(storeId, user.user_id)])
            .then(([options, assignment]) => {
                if (cancelled) return;
                setCatalog(options);
                setSettings(assignment);
                setLoadStatus('ready');
            })
            .catch(() => {
                if (cancelled) return;
                setError('Unable to load access. Close and try again.');
                setLoadStatus('error');
            });
        return () => { cancelled = true; };
    }, [storeId, user.user_id]);
    const toggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = event.target;
        setSettings(current => current && ({ ...current, access_scopes: checked
            ? [...current.access_scopes, name] : current.access_scopes.filter(key => key !== name) }));
    };
    const save = async () => {
        if (!settings || role !== 'manager') return;
        setSaving(true); setError('');
        try {
            await storeAccessService.save(storeId, user.user_id, settings.access_scopes, settings.receive_daily_reconciliation_email);
            toast.success(`Access and email settings saved for ${user.display_name || 'this user'}.`);
            onClose();
        }
        catch (err: any) { setError(err?.response?.data?.detail || 'Unable to save access. Please try again.'); }
        finally { setSaving(false); }
    };
    return <Dialog open onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth
        aria-labelledby="store-user-access-title" PaperProps={{ sx: adminDialogSx }}>
        <DialogTitle id="store-user-access-title" sx={adminDialogTitleSx}>Access and daily email</DialogTitle>
        <DialogContent sx={adminCombineSx(adminDialogContentSx, { p: 2 })}>
            <Stack spacing={2}>
                <Box sx={{ pb: 2, borderBottom: `1px solid ${adminColors.divider}` }}>
                    <Typography sx={{ ...adminTypography.bodyStrong, overflowWrap: 'anywhere' }}>{user.display_name}</Typography>
                    <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText, textTransform: 'capitalize', mt: 0.5 }}>{role}</Typography>
                </Box>
                <Typography sx={{ ...adminTypography.body, color: adminColors.mutedText }}>
                    Choose which features this manager can view and whether they receive daily email.
                </Typography>
                {error && <Alert severity="error">{error}</Alert>}
                {loadStatus === 'loading' ? <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
                    <CircularProgress size={20} aria-label="Loading access" />
                    <Typography sx={adminTypography.helper}>Loading access…</Typography>
                </Stack> : loadStatus === 'ready' && role !== 'manager' ? <Alert severity="info">Owners have full access and daily email automatically. Only manager settings can be changed.</Alert> : settings && <Box sx={{ border: `1px solid ${adminColors.border}`, borderRadius: adminRadii.md, overflow: 'hidden' }}>
                    {catalog.filter(scope => scope.eligible_roles.includes('manager')).map(scope => <FormControlLabel key={scope.key} label={scope.label}
                        sx={{
                            m: 0, px: 1.5, py: 1, gap: 1, display: 'flex', minHeight: 48,
                            backgroundColor: settings.access_scopes.includes(scope.key) ? adminColors.primarySoft : adminColors.surface,
                            '& + &': { borderTop: `1px solid ${adminColors.divider}` },
                            '& .MuiFormControlLabel-label': adminTypography.label,
                        }}
                        control={<Checkbox name={scope.key} checked={settings.access_scopes.includes(scope.key)}
                            sx={adminCheckboxSx} disabled={saving || !scope.eligible_roles.includes(role)} onChange={toggle} />}
                    />)}
                    <FormControlLabel label="Receive Daily Email"
                        sx={{ m: 0, px: 1.5, py: 1, gap: 1, display: 'flex', minHeight: 48,
                            borderTop: `1px solid ${adminColors.divider}`, '& .MuiFormControlLabel-label': adminTypography.label }}
                        control={<Checkbox checked={settings.receive_daily_reconciliation_email} disabled={saving} sx={adminCheckboxSx}
                            onChange={(_, checked) => setSettings({ ...settings, receive_daily_reconciliation_email: checked })} />} />
                </Box>}
            </Stack>
        </DialogContent>
        <DialogActions sx={adminDialogActionsSx}>
            <Button onClick={onClose} disabled={saving} variant="outlined" sx={adminButtonSx}>Cancel</Button>
            <Button onClick={save} disabled={loadStatus !== 'ready' || saving || role !== 'manager'} variant="contained"
                startIcon={saving ? <CircularProgress size={14} color="inherit" aria-label="Saving access" /> : undefined}
                disableElevation sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
    </Dialog>;
}
